import React, { useState, useEffect } from "react";
import {
  NON_DRAFT_COMPLETION_STATUS_OPTIONS,
  CHECK_IN_STATUS_OPTIONS,
  CONFIRMATION_STATUS_OPTIONS,
  defaultMaxStartDateRangeDate,
  defaultStartDateRangeDate
} from "src/helpers";
import { PurchaseOrdersFilters, DateTimeRange } from "src/types";
import { CreationDateSortOptions, PurchaseOrderColumnHeader } from "src/enums";
import { DropdownField } from "src/components/ui/inputs";
import { Button } from "src/shadcn/ui/button";
import { Input } from "src/shadcn/ui/input";
import { Badge } from "src/shadcn/ui/badge";
import { Trash2 } from "lucide-react";

interface FilterVendorPurchaseOrdersProps {
  onFilterChange: (filters: PurchaseOrdersFilters) => void;
  currentFilter?: PurchaseOrdersFilters;
  dateTimeRange: DateTimeRange;
  onDateTimeRangeChange: (range: DateTimeRange) => void;
  deliveryDateTimeRange?: DateTimeRange;
  onDeliveryDateTimeRangeChange?: (range: DateTimeRange) => void;
  receiptDateTimeRange?: DateTimeRange;
  onReceiptDateTimeRangeChange?: (range: DateTimeRange) => void;
  sortOption: CreationDateSortOptions;
  onSortChange: (option: CreationDateSortOptions) => void;
  columnsToHide: PurchaseOrderColumnHeader[];
  onColumnsToHideChange: (columns: PurchaseOrderColumnHeader[]) => void;
  allColumns: PurchaseOrderColumnHeader[];
  onClose?: () => void;
  isOpen?: boolean;
}

export const FilterVendorPurchaseOrders: React.FC<FilterVendorPurchaseOrdersProps> = ({
  onFilterChange,
  currentFilter,
  dateTimeRange,
  onDateTimeRangeChange,
  deliveryDateTimeRange,
  onDeliveryDateTimeRangeChange,
  receiptDateTimeRange,
  onReceiptDateTimeRangeChange,
  sortOption,
  onSortChange,
  columnsToHide,
  onColumnsToHideChange,
  allColumns,
  onClose,
  isOpen
}) => {
  // Local state for date range and sort (only applied on "Apply Changes")
  const [localDateTimeRange, setLocalDateTimeRange] = useState<DateTimeRange>(dateTimeRange);
  const [localDeliveryDateTimeRange, setLocalDeliveryDateTimeRange] = useState<DateTimeRange>(
    deliveryDateTimeRange ?? { start: undefined, end: undefined }
  );
  const [localReceiptDateTimeRange, setLocalReceiptDateTimeRange] = useState<DateTimeRange>(
    receiptDateTimeRange ?? { start: undefined, end: undefined }
  );
  const [localSortOption, setLocalSortOption] = useState<CreationDateSortOptions>(sortOption);

  const [filters, setFilters] = useState<Partial<PurchaseOrdersFilters>>({
    completionStatusFilter: currentFilter?.completionStatusFilter,
    checkInStatusFilter: currentFilter?.checkInStatusFilter,
    confirmationStatusFilter: currentFilter?.confirmationStatusFilter,
    hasTrackingNo: currentFilter?.hasTrackingNo ?? undefined,
    hasInvoice: currentFilter?.hasInvoice ?? undefined,
    isOverdueDelivery: currentFilter?.isOverdueDelivery ?? undefined,
    isInvoicePaymentDue: currentFilter?.isInvoicePaymentDue ?? undefined
  });

  // Reset local state to match applied filters when sidebar opens
  useEffect(() => {
    if (isOpen) {
      setLocalDateTimeRange(dateTimeRange);
      setLocalDeliveryDateTimeRange(deliveryDateTimeRange ?? { start: undefined, end: undefined });
      setLocalReceiptDateTimeRange(receiptDateTimeRange ?? { start: undefined, end: undefined });
      setLocalSortOption(sortOption);
      setFilters({
        completionStatusFilter: currentFilter?.completionStatusFilter,
        checkInStatusFilter: currentFilter?.checkInStatusFilter,
        confirmationStatusFilter: currentFilter?.confirmationStatusFilter,
        hasTrackingNo: currentFilter?.hasTrackingNo ?? undefined,
        hasInvoice: currentFilter?.hasInvoice ?? undefined,
        isOverdueDelivery: currentFilter?.isOverdueDelivery ?? undefined,
        isInvoicePaymentDue: currentFilter?.isInvoicePaymentDue ?? undefined
      });
    }
  }, [isOpen, dateTimeRange, deliveryDateTimeRange, receiptDateTimeRange, sortOption, currentFilter]);

  const handleFilterChange = <K extends keyof PurchaseOrdersFilters>(key: K, value: PurchaseOrdersFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    const updatedFilters: PurchaseOrdersFilters = {
      ...filters,
      hasTrackingNo: filters.hasTrackingNo ?? undefined,
      hasInvoice: filters.hasInvoice ?? undefined,
      isOverdueDelivery: filters.isOverdueDelivery ?? undefined,
      isInvoicePaymentDue: filters.isInvoicePaymentDue ?? undefined,
      filterPopoverVisible: false
    } as PurchaseOrdersFilters;

    // Apply date range and sort changes
    onDateTimeRangeChange(localDateTimeRange);
    onDeliveryDateTimeRangeChange?.(localDeliveryDateTimeRange);
    onReceiptDateTimeRangeChange?.(localReceiptDateTimeRange);
    onSortChange(localSortOption);
    onFilterChange(updatedFilters);

    // Close the sidebar
    onClose?.();
  };

  const handleClearAllFilters = () => {
    const clearedFilters: PurchaseOrdersFilters = {
      completionStatusFilter: undefined,
      checkInStatusFilter: undefined,
      confirmationStatusFilter: undefined,
      hasTrackingNo: undefined,
      hasInvoice: undefined,
      isOverdueDelivery: undefined,
      isInvoicePaymentDue: undefined,
      filterPopoverVisible: false
    };
    setFilters(clearedFilters);

    // Reset date range and sort to defaults
    const defaultRange = { start: defaultStartDateRangeDate(), end: undefined };
    setLocalDateTimeRange(defaultRange);
    onDateTimeRangeChange(defaultRange);

    const emptyRange = { start: undefined, end: undefined };
    setLocalDeliveryDateTimeRange(emptyRange);
    onDeliveryDateTimeRangeChange?.(emptyRange);
    setLocalReceiptDateTimeRange(emptyRange);
    onReceiptDateTimeRangeChange?.(emptyRange);

    setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC);
    onSortChange(CreationDateSortOptions.CREATION_DATE_DESC);

    onFilterChange(clearedFilters);
  };

  const countActiveFilters = () => {
    let count = 0;
    if (filters.completionStatusFilter) count++;
    if (filters.checkInStatusFilter) count++;
    if (filters.confirmationStatusFilter) count++;
    if (filters.hasTrackingNo !== undefined) count++;
    if (filters.hasInvoice !== undefined) count++;
    if (filters.isOverdueDelivery !== undefined) count++;
    if (filters.isInvoicePaymentDue !== undefined) count++;
    if (localDateTimeRange.start) {
      const defaultStart = defaultStartDateRangeDate();
      const isDefault =
        localDateTimeRange.start.getFullYear() === defaultStart.getFullYear() &&
        localDateTimeRange.start.getMonth() === defaultStart.getMonth() &&
        localDateTimeRange.start.getDate() === defaultStart.getDate() &&
        localDateTimeRange.start.getHours() === defaultStart.getHours() &&
        localDateTimeRange.start.getMinutes() === defaultStart.getMinutes();
      if (!isDefault) count++;
    }
    if (localDateTimeRange.end) count++;
    if (localDeliveryDateTimeRange.start) count++;
    if (localDeliveryDateTimeRange.end) count++;
    if (localReceiptDateTimeRange.start) count++;
    if (localReceiptDateTimeRange.end) count++;
    if (localSortOption !== CreationDateSortOptions.CREATION_DATE_DESC) count++;
    return count;
  };

  const filteredConfirmationOptions = CONFIRMATION_STATUS_OPTIONS.filter(
    (option) => option !== "GXO Error" && option !== "TBC (Non-Portal)" && option !== "Overdue (Non-Portal)"
  );

  const formatDateForInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDateForDateInput = (date?: Date) => {
    if (!date) return "";
    try {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  const handleDateChange = (field: "start" | "end", value: string, rangeType: "creation" | "delivery" | "receipt") => {
    if (rangeType !== "creation") {
      const currentRange = rangeType === "delivery" ? localDeliveryDateTimeRange : localReceiptDateTimeRange;
      const setRange = rangeType === "delivery" ? setLocalDeliveryDateTimeRange : setLocalReceiptDateTimeRange;

      if (!value) {
        setRange({
          ...currentRange,
          [field]: undefined
        });
        return;
      }

      const newDate = new Date(`${value}T00:00:00.000Z`);

      if (field === "start") {
        setRange({
          ...currentRange,
          start: newDate,
          end: currentRange.end && newDate > currentRange.end ? newDate : currentRange.end
        });
      } else {
        setRange({
          ...currentRange,
          end: newDate,
          start: currentRange.start && newDate < currentRange.start ? newDate : currentRange.start
        });
      }
      return;
    }

    if (!value) {
      const newRange = {
        ...localDateTimeRange,
        [field]: undefined
      };
      setLocalDateTimeRange(newRange);
      return;
    }

    const newDate = new Date(value);
    const maxStartDate = defaultMaxStartDateRangeDate();
    const maxEndDate = new Date();

    if (field === "start") {
      // Start date should be >= maxStartDate (6 months ago) and <= end date or today
      const maxAllowedStart = localDateTimeRange.end ? new Date(localDateTimeRange.end) : maxEndDate;
      const clampedStart =
        newDate < maxStartDate ? maxStartDate : newDate > maxAllowedStart ? maxAllowedStart : newDate;

      setLocalDateTimeRange({
        start: clampedStart,
        end: localDateTimeRange.end && clampedStart > localDateTimeRange.end ? clampedStart : localDateTimeRange.end
      });
    } else {
      // End date should be >= start date and <= today
      const minAllowedEnd = localDateTimeRange.start ? new Date(localDateTimeRange.start) : maxStartDate;
      const clampedEnd = newDate < minAllowedEnd ? minAllowedEnd : newDate > maxEndDate ? maxEndDate : newDate;

      setLocalDateTimeRange({
        ...localDateTimeRange,
        end: clampedEnd
      });
    }
  };

  return (
    <div className="space-y-5">
      {/* Filters */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <div className="flex items-center gap-1.5">
            <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Filters</h3>
            {countActiveFilters() > 0 && (
              <Badge variant="default" className="bg-secondary-900 text-white text-xs px-1.5 py-0 h-4">
                {countActiveFilters()}
              </Badge>
            )}
          </div>
          {countActiveFilters() > 0 && (
            <button
              onClick={handleClearAllFilters}
              className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </button>
          )}
        </div>

        <div className="space-y-2.5">
          <DropdownField
            label="Confirmation Status"
            value={filters.confirmationStatusFilter}
            onChange={(value) => handleFilterChange("confirmationStatusFilter", value as any)}
            options={filteredConfirmationOptions}
          />

          <DropdownField
            label="Completion Status"
            value={filters.completionStatusFilter}
            onChange={(value) => handleFilterChange("completionStatusFilter", value as any)}
            options={NON_DRAFT_COMPLETION_STATUS_OPTIONS}
          />

          <DropdownField
            label="Check-in Status"
            value={filters.checkInStatusFilter}
            onChange={(value) => handleFilterChange("checkInStatusFilter", value as any)}
            options={CHECK_IN_STATUS_OPTIONS}
          />

          <DropdownField
            label="Has Tracking"
            value={filters.hasTrackingNo === undefined ? undefined : filters.hasTrackingNo ? "Yes" : "No"}
            onChange={(value) => handleFilterChange("hasTrackingNo", value === undefined ? undefined : value === "Yes")}
            options={["Yes", "No"]}
          />

          <DropdownField
            label="Has Invoice"
            value={filters.hasInvoice === undefined ? undefined : filters.hasInvoice ? "Yes" : "No"}
            onChange={(value) => handleFilterChange("hasInvoice", value === undefined ? undefined : value === "Yes")}
            options={["Yes", "No"]}
          />

          <DropdownField
            label="Overdue Delivery"
            value={filters.isOverdueDelivery === undefined ? undefined : filters.isOverdueDelivery ? "Yes" : "No"}
            onChange={(value) =>
              handleFilterChange("isOverdueDelivery", value === undefined ? undefined : value === "Yes")
            }
            options={["Yes", "No"]}
          />

          <DropdownField
            label="Payment Due"
            value={filters.isInvoicePaymentDue === undefined ? undefined : filters.isInvoicePaymentDue ? "Yes" : "No"}
            onChange={(value) =>
              handleFilterChange("isInvoicePaymentDue", value === undefined ? undefined : value === "Yes")
            }
            options={["Yes", "No"]}
          />

          {/* Creation Date Range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - Start
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.start)}
              onChange={(e) => handleDateChange("start", e.target.value, "creation")}
              min={formatDateForInput(defaultMaxStartDateRangeDate())}
              max={formatDateForInput(localDateTimeRange.end || new Date())}
              className="text-sm h-9"
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">
              Creation Date - End
            </label>
            <Input
              type="datetime-local"
              value={formatDateForInput(localDateTimeRange.end)}
              onChange={(e) => handleDateChange("end", e.target.value, "creation")}
              min={formatDateForInput(localDateTimeRange.start || defaultMaxStartDateRangeDate())}
              max={formatDateForInput(new Date())}
              className="text-sm h-9"
            />
          </div>

          {/* Delivery Date Range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Delivery Date</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="date"
                value={formatDateForDateInput(localDeliveryDateTimeRange.start)}
                onChange={(e) => handleDateChange("start", e.target.value, "delivery")}
                className="text-sm h-9 flex-1"
                placeholder="Start"
              />
              <span className="text-sm text-secondary-600">_</span>
              <Input
                type="date"
                value={formatDateForDateInput(localDeliveryDateTimeRange.end)}
                onChange={(e) => handleDateChange("end", e.target.value, "delivery")}
                className="text-sm h-9 flex-1"
                placeholder="End"
              />
            </div>
          </div>

          {/* Receipt Date Range */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Receipt Date</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="date"
                value={formatDateForDateInput(localReceiptDateTimeRange.start)}
                onChange={(e) => handleDateChange("start", e.target.value, "receipt")}
                className="text-sm h-9 flex-1"
                placeholder="Start"
              />
              <span className="text-sm text-secondary-600">_</span>
              <Input
                type="date"
                value={formatDateForDateInput(localReceiptDateTimeRange.end)}
                onChange={(e) => handleDateChange("end", e.target.value, "receipt")}
                className="text-sm h-9 flex-1"
                placeholder="End"
              />
            </div>
          </div>

          {/* Sort */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-secondary-700 uppercase tracking-wide">Sort Order</label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_DESC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_DESC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Newest
              </button>
              <button
                type="button"
                onClick={() => setLocalSortOption(CreationDateSortOptions.CREATION_DATE_ASC)}
                className={`flex-1 px-2 py-1.5 text-xs font-medium rounded transition-all ${
                  localSortOption === CreationDateSortOptions.CREATION_DATE_ASC
                    ? "bg-secondary-900 text-white shadow-sm"
                    : "bg-secondary-50 text-secondary-600 hover:bg-secondary-100"
                }`}
              >
                Oldest
              </button>
            </div>
          </div>
        </div>

        {/* Apply Button */}
        <div className="sticky -bottom-4 pt-3 pb-1 bg-background border-t border-secondary-200 -mx-6 px-6 z-10">
          <Button
            onClick={handleApplyFilters}
            className="w-full bg-secondary-900 hover:bg-secondary-800 h-9 text-sm font-medium text-white"
          >
            Apply Changes
          </Button>
        </div>
      </div>

      {/* Column Visibility */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between pb-1.5 border-b border-secondary-200">
          <h3 className="text-xs font-semibold text-secondary-900 uppercase tracking-wide">Show/Hide Columns</h3>
          <div className="flex items-center gap-2">
            {columnsToHide.length > 0 && (
              <button
                onClick={() => onColumnsToHideChange([])}
                className="text-xs text-secondary-600 hover:text-secondary-900 flex items-center gap-1 transition-colors"
              >
                <Trash2 className="h-3 w-3" />
                Reset
              </button>
            )}
            <span className="text-xs text-secondary-600 font-medium">
              {allColumns.length - columnsToHide.length}/{allColumns.length}
            </span>
          </div>
        </div>
        <div className="space-y-1 max-h-44 overflow-y-auto pr-1 scrollbar-thin">
          {allColumns.map((column) => {
            const isVisible = !columnsToHide.includes(column);
            return (
              <button
                key={column}
                onClick={() => {
                  if (isVisible) {
                    onColumnsToHideChange([...columnsToHide, column]);
                  } else {
                    onColumnsToHideChange(columnsToHide.filter((c) => c !== column));
                  }
                }}
                className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-all ${
                  isVisible
                    ? "bg-secondary-50 text-secondary-900 hover:bg-secondary-100"
                    : "bg-transparent text-secondary-400 hover:bg-secondary-50"
                }`}
              >
                <span className="font-medium truncate">{column}</span>
                <Badge
                  variant={isVisible ? "default" : "outline"}
                  className={`text-xs px-1.5 py-0 h-4 flex-shrink-0 ml-2 ${isVisible ? "bg-green-500 hover:bg-green-600" : ""}`}
                >
                  {isVisible ? "Visible" : "Hidden"}
                </Badge>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
